import React, { useState, useEffect } from "react";
import styled from "styled-components";
import ErrorCard from "../ErrorCard";

const Container = styled.div`
  margin: 32px 0;
`;

const SectionTitle = styled.h3`
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--zyvgo-text-primary, #222222);
  margin: 0 0 12px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const WeightInputGroup = styled.div`
  margin-bottom: 24px;
`;

const InputLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--zyvgo-text-primary, #222222);
  margin-bottom: 8px;
  display: block;
`;

const NumberInput = styled.input`
  width: 100%;
  padding: 16px 20px;
  border: 2px solid var(--zyvgo-border, #e1e5e9);
  border-radius: var(--zyvgo-radius-sm, 6px);
  font-size: 1rem;
  font-family: var(--zyvgo-font-family);
  color: var(--zyvgo-text-primary, #222222);
  background-color: #ffffff;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--zyvgo-primary, #222222);
    box-shadow: 0 0 0 3px rgba(34, 34, 34, 0.1);
  }

  &::placeholder {
    color: #999999;
  }
`;

const HeightSection = styled.div`
  margin-bottom: 24px;
`;

const InputRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const BMIResultCard = styled.div`
  background-color: ${(props) => {
    if (props.$bmiCategory === "underweight") return "#FFF8F1";
    if (props.$bmiCategory === "normal") return "#F0FFF4";
    if (props.$bmiCategory === "overweight") return "#FFF8F1";
    if (props.$bmiCategory === "obese") return "#FFF5F5";
    return "#F8F9FA";
  }};
  border: 2px solid
    ${(props) => {
    if (props.$bmiCategory === "underweight") return "#FED7AA";
    if (props.$bmiCategory === "normal") return "#50b37a";
    if (props.$bmiCategory === "overweight") return "#F4D2B1";
    if (props.$bmiCategory === "obese") return "#FEB2B2";
    return "#e1e5e9";
  }};
  border-radius: var(--zyvgo-radius-md, 8px);
  padding: 20px;
  margin-top: 24px;
`;

const BMITitle = styled.h3`
  margin: 0 0 8px 0;
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--zyvgo-text-primary, #222222);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const BMIValue = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 16px;
`;

const BMINumber = styled.span`
  font-size: 2rem;
  font-weight: 700;
  color: var(--zyvgo-primary, #222222);
`;

const BMICategory = styled.span`
  font-size: 1rem;
  font-weight: 600;
  color: ${(props) => {
    if (props.category === "underweight") return "#DD6B20";
    if (props.category === "normal") return "#50b37a";
    if (props.category === "overweight") return "#DD6B20";
    if (props.category === "obese") return "#e74c3c";
    return "#666666";
  }};
  text-transform: capitalize;
`;

const BMIRangeBar = styled.div`
  width: 100%;
  height: 32px;
  background: linear-gradient(
    to right,
    #fed7aa 0%,
    #fed7aa 25%,
    #50b37a 25%,
    #50b37a 50%,
    #f4d2b1 50%,
    #f4d2b1 75%,
    #feb2b2 75%
  );
  border-radius: 16px;
  position: relative;
  margin-bottom: 12px;
`;

const BMIIndicator = styled.div`
  position: absolute;
  width: 4px;
  height: 40px;
  background-color: var(--zyvgo-primary, #222222);
  top: -4px;
  left: ${(props) => props.position}%;
  transform: translateX(-50%);
  border-radius: 2px;

  &::after {
    content: "";
    position: absolute;
    top: -6px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 8px solid var(--zyvgo-primary, #222222);
  }
`;

const BMILabels = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 4px;
  font-size: 0.75rem;
  color: var(--zyvgo-text-secondary, #666666);
  text-align: center;
`;

const ErrorMessage = styled.div`
  color: var(--zyvgo-error, #e74c3c);
  font-size: 0.875rem;
  margin-top: 12px;
  font-weight: 500;
`;

const HeightWeight = ({
  question,
  value,
  onChange,
  error,
  onBMIError,
  questionnaire,
  updateResponse,
  allResponses
}) => {
  const currentValue = value || {};

  // Find the separate weight question (the one that comes after height_weight in the original questionnaire)
  const weightQuestion = questionnaire?.questions?.find(
    q => q.question_type === 'number' &&
      q.question_text.toLowerCase().includes('weight') &&
      q.id !== question.id // Make sure it's not the current question
  );

  // Get weight from the separate weight question response, or from current value
  const initialWeight = weightQuestion && allResponses?.[weightQuestion.id]
    ? allResponses[weightQuestion.id]
    : currentValue.weight || "";

  const [weight, setWeight] = useState(initialWeight);
  const [bmiData, setBMIData] = useState(null);
  const [bmiError, setBMIError] = useState(null);

  const calculateBMI = (feet, inches, weightLbs) => {
    // Check for valid values - inches can be 0, so use explicit checks
    if (!feet || inches === "" || inches === undefined || inches === null || !weightLbs || weightLbs < 50)
      return null;

    const totalInches = parseInt(feet) * 12 + parseInt(inches);
    const bmi = (parseFloat(weightLbs) / (totalInches * totalInches)) * 703;
    const bmiRounded = Math.round(bmi * 10) / 10;

    let category = "normal";
    if (bmiRounded < 18.5) category = "underweight";
    else if (bmiRounded >= 18.5 && bmiRounded < 25) category = "normal";
    else if (bmiRounded >= 25 && bmiRounded < 30) category = "overweight";
    else if (bmiRounded >= 30) category = "obese";

    let position = 50;
    if (bmiRounded < 18.5) position = (bmiRounded / 18.5) * 25;
    else if (bmiRounded < 25)
      position = 25 + ((bmiRounded - 18.5) / (25 - 18.5)) * 25;
    else if (bmiRounded < 30)
      position = 50 + ((bmiRounded - 25) / (30 - 25)) * 25;
    else position = Math.min(75 + ((bmiRounded - 30) / 20) * 25, 97);

    return {
      bmi: bmiRounded,
      category,
      position,
    };
  };

  const checkBMIEligibility = (bmi) => {
    const bmiMin = question?.validation?.bmi_min;

    // If admin configured a min limit, disqualify when BMI is below it
    if (bmiMin !== undefined && bmi < bmiMin) {
      return {
        eligible: false,
        message: `Your BMI is ${bmi}. Our program is designed for individuals with a BMI of ${bmiMin} or higher.`,
      };
    }

    return { eligible: true };
  };

  // Sync weight to the separate weight question on mount/update
  useEffect(() => {
    if (weight && weightQuestion && updateResponse) {
      // Make sure the weight question response is set
      if (allResponses?.[weightQuestion.id] !== weight) {
        updateResponse(weightQuestion.id, weight);
      }
    }
  }, [weight, weightQuestion, updateResponse, allResponses]);

  // Calculate BMI whenever height or weight changes
  useEffect(() => {
    // Check for valid values - inches can be 0, so check explicitly for empty/undefined/null
    if (currentValue.feet && currentValue.inches !== "" && currentValue.inches !== undefined && currentValue.inches !== null && weight) {
      const result = calculateBMI(
        currentValue.feet,
        currentValue.inches,
        weight
      );
      setBMIData(result);

      if (result) {
        // 🆕 Auto-populate the BMI question with calculated value (only if changed)
        if (questionnaire && updateResponse && allResponses) {
          const bmiQuestion = questionnaire.questions.find(
            q => q.question_type === "number" &&
              (q.question_text.toLowerCase().includes("bmi") ||
                q.question_text.toLowerCase().includes("your bmi is"))
          );

          if (bmiQuestion) {
            // Only update if the value has changed to prevent infinite loop
            const currentBMIResponse = allResponses[bmiQuestion.id];
            if (currentBMIResponse !== result.bmi) {
              updateResponse(bmiQuestion.id, result.bmi);
              console.log(`🔢 HeightWeight: Auto-populated BMI question (${bmiQuestion.id}) with value: ${result.bmi}`);
            }
          }
        }

        const eligibility = checkBMIEligibility(result.bmi);
        if (!eligibility.eligible) {
          setBMIError(eligibility.message);
          if (onBMIError) {
            onBMIError(eligibility.message);
          }
        } else {
          setBMIError(null);
          if (onBMIError) {
            onBMIError(null);
          }
        }
      }
    } else {
      setBMIData(null);
      setBMIError(null);
      if (onBMIError) {
        onBMIError(null);
      }
    }
  }, [currentValue.feet, currentValue.inches, weight, onBMIError, questionnaire, updateResponse, allResponses]);

  const handleWeightChange = (newWeight) => {
    setWeight(newWeight);

    // Update the height question response (for BMI calculation on frontend)
    onChange({
      ...currentValue,
      weight: newWeight,
    });

    // ALSO update the separate weight question response (for backend)
    if (weightQuestion && updateResponse) {
      updateResponse(weightQuestion.id, newWeight);
    }
  };

  const handleFeetChange = (feet) => {
    const feetValue = parseInt(feet) || "";
    onChange({
      ...currentValue,
      feet: feetValue,
    });
  };

  const handleInchesChange = (inches) => {
    // Use ternary to allow 0 as a valid value (|| would treat 0 as falsy)
    const parsed = parseInt(inches);
    const inchesValue = isNaN(parsed) ? "" : parsed;
    onChange({
      ...currentValue,
      inches: inchesValue,
    });
  };

  return (
    <Container>
      {/* Weight Input */}
      <WeightInputGroup>
        <SectionTitle>What is your weight in pounds?</SectionTitle>
        <InputLabel>Weight (lbs)</InputLabel>
        <NumberInput
          type="number"
          min="50"
          max="800"
          value={weight}
          onChange={(e) => handleWeightChange(e.target.value)}
          placeholder="160"
        />
      </WeightInputGroup>

      {/* Height Input */}
      <HeightSection>
        <SectionTitle>What is your height in feet and inches?</SectionTitle>
        <InputRow>
          <InputGroup>
            <InputLabel>Height (Feet)</InputLabel>
            <NumberInput
              type="number"
              min="3"
              max="8"
              value={currentValue.feet || ""}
              onChange={(e) => handleFeetChange(e.target.value)}
              placeholder="5"
            />
          </InputGroup>
          <InputGroup>
            <InputLabel>Height (Inches)</InputLabel>
            <NumberInput
              type="number"
              min="0"
              max="11"
              value={currentValue.inches ?? ""}
              onChange={(e) => handleInchesChange(e.target.value)}
              placeholder="8"
            />
          </InputGroup>
        </InputRow>
      </HeightSection>

      {/* BMI Result Display */}
      {bmiData && (
        <BMIResultCard $bmiCategory={bmiData.category}>
          <BMITitle>Your BMI Result</BMITitle>
          <BMIValue>
            <BMINumber>{bmiData.bmi}</BMINumber>
            <BMICategory category={bmiData.category}>
              {bmiData.category}
            </BMICategory>
          </BMIValue>
          <BMIRangeBar>
            <BMIIndicator position={bmiData.position} />
          </BMIRangeBar>
          <BMILabels>
            <div>
              Lower Weight
              <br />
              &lt;18.5
            </div>
            <div>
              Average Weight
              <br />
              18.5-26.9
            </div>
            <div>
              Higher Weight
              <br />
              27-29.9
            </div>
            <div>
              Over Weight
              <br />
              ≥30
            </div>
          </BMILabels>
        </BMIResultCard>
      )}

      {/* BMI Error Message */}
      {bmiError && (
        <ErrorCard
          title="You don't qualify for our program at this time"
          message={bmiError}
          variant="warning"
        />
      )}

      {error && !bmiError && <ErrorMessage>{error}</ErrorMessage>}
    </Container>
  );
};

export default HeightWeight;
